import React, { useState } from 'react';
import { PageHeader, PageTitle } from '../../../styles/components';
import { applicationStatus } from '../../../data/mock/studentInternships';
import { FaSearch, FaFilter, FaTimes, FaRegCalendarAlt, FaInfoCircle, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaUserTie } from 'react-icons/fa';
import styled, { css } from 'styled-components';
import { Card } from '../../../styles/components/common/cards';
import { Button, SecondaryButton } from '../../../styles/components/common/buttons';
import { ModalOverlay, ModalContent, ModalHeader, ModalTitle, CloseButton, ModalBody, ModalFooter } from '../../../styles/components/common/modals';
import { fadeInAnimation } from '../../../styles/components/common/animations';
import StatusBadge from '../../../styles/components/StatusBadge';

// Styled components for a modern dashboard feel
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  animation: ${fadeInAnimation} 0.5s ease-out;
`;

const FilterCard = styled(Card)`
  margin-bottom: 2rem;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterLabel = styled.label`
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(15, 23, 42, 0.3);
  color: rgba(255, 255, 255, 0.9);
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SelectFilter = styled.select`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(15, 23, 42, 0.3);
  color: rgba(255, 255, 255, 0.9);
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
  
  option {
    background: rgba(15, 23, 42, 0.9);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.25rem;
`;

const ClearButton = styled(SecondaryButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ApplicationsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-top: 2rem;
`;

const ApplicationCard = styled(Card)`
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover:after {
    opacity: 1;
  }
`;

const ApplicationFlex = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: center;
`;

const LogoContainer = styled.div`
  flex-shrink: 0;
  width: 70px;
  height: 70px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
`;

const CompanyLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${ApplicationCard}:hover & {
    transform: scale(1.05);
  }
`;

const ApplicationContent = styled.div`
  flex: 1;
`;

const ApplicationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const PositionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 0.35rem 0;
  color: rgba(255, 255, 255, 0.9);
`;

const CompanyName = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 0.5rem 0;
  font-weight: 500;
`;

const ApplicationMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.9rem;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: rgba(255, 255, 255, 0.7);
`;

const EmptyState = styled(Card)`
  text-align: center;
  padding: 3rem 2rem;
`;

const EmptyStateText = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 1rem 0;
`;

// Status cards styling based on application status
const StatusCard = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border-radius: 12px;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  border-left: 4px solid ${({ status }) => {
    switch (status.toLowerCase()) {
      case 'pending': return '#F4B400'; // Amber for pending
      case 'interview': return '#4285F4'; // Blue for interview
      case 'accepted': return '#0F9D58'; // Green for accepted
      case 'rejected': return '#DB4437'; // Red for rejected
      default: return '#5F6368'; // Grey for default
    }
  }};
  
  /* Match the colors from the StatusBadge component */
  ${({ status }) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return css`
          background: linear-gradient(90deg, rgba(244, 180, 0, 0.1) 0%, rgba(15, 23, 42, 0.6) 50%);
        `;
      case 'interview':
        return css`
          background: linear-gradient(90deg, rgba(66, 133, 244, 0.1) 0%, rgba(15, 23, 42, 0.6) 50%);
        `;
      case 'finalized':
        return css`
          background: linear-gradient(90deg, rgba(138, 43, 226, 0.1) 0%, rgba(15, 23, 42, 0.6) 50%);
        `;
      case 'accepted':
        return css`
          background: linear-gradient(90deg, rgba(15, 157, 88, 0.1) 0%, rgba(15, 23, 42, 0.6) 50%);
        `;
      case 'rejected':
        return css`
          background: linear-gradient(90deg, rgba(219, 68, 55, 0.1) 0%, rgba(15, 23, 42, 0.6) 50%);
        `;
      default:
        return css`
          background: rgba(15, 23, 42, 0.6);
        `;
    }
  }}
`;

const DetailsCard = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border-radius: 12px;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(99, 102, 241, 0.25);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const NextStepsCard = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border-radius: 12px;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(15, 157, 88, 0.3);
  border-left: 4px solid #0F9D58;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: linear-gradient(90deg, rgba(15, 157, 88, 0.1) 0%, rgba(15, 23, 42, 0.6) 50%);
`;

const CardTitle = styled.h3`
  font-size: 1.15rem;
  margin: 0 0 1rem 0;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Modal company info section
const ModalCompanyInfo = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
`;

const ModalLogoContainer = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(99, 102, 241, 0.2);
  background: rgba(15, 23, 42, 0.3);
`;

const ButtonsContainer = styled(ModalFooter)`
  justify-content: center;
`;

const CloseModalButton = styled(Button)`
  min-width: 120px;
`;

const ApplicationsList = () => {
  const [applications] = useState(applicationStatus);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter applications
  const filteredApplications = applications.filter(application => {
    const matchesSearch = 
      (application.position?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
      (application.company?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === '' || application.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  // View application details
  const handleViewApplication = (application) => {
    setSelectedApplication(application);
  };

  // Close application details modal
  const closeApplicationDetails = () => {
    setSelectedApplication(null);
  };

  // Status icon
  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <FaHourglassHalf />;
      case 'interview':
        return <FaUserTie />;
      case 'finalized':
        return <FaInfoCircle />;
      case 'accepted':
        return <FaCheckCircle />;
      case 'rejected':
        return <FaTimesCircle />;
      default:
        return <FaInfoCircle />;
    }
  };

  // Status description
  const getStatusDescription = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'Your application has been submitted and is waiting to be reviewed by the company.';
      case 'interview':
        return 'Your application has been reviewed and you have been invited for an interview.';
      case 'finalized':
        return 'Congratulations! Your application has been shortlisted - you are one of the top candidates. The company is making final decisions.';
      case 'accepted':
        return 'Congratulations! Your application has been accepted. The company will contact you with further details.';
      case 'rejected':
        return 'Unfortunately, your application was not selected for this position.';
      default:
        return '';
    }
  };

  return (
    <Container>
      <PageHeader>
        <PageTitle>My Applications</PageTitle>
      </PageHeader>
      
      {/* Search and Filter Section */}
      <FilterCard>
        <FilterGrid>
          <FilterGroup>
            <FilterLabel>
              <FaSearch /> Search by position or company
            </FilterLabel>
            <SearchInput 
              type="text" 
              placeholder="Search..." 
              value={searchTerm}
              onChange={handleSearch}
            />
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>
              <FaFilter /> Filter by Status
            </FilterLabel>
            <SelectFilter 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="interview">Interview</option>
              <option value="finalized">Finalized</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </SelectFilter>
          </FilterGroup>
        </FilterGrid>
        

      </FilterCard>
      
      {/* Applications List */}
      <ApplicationsGrid>
        {filteredApplications.length === 0 ? (
          <EmptyState>
            <EmptyStateText>No applications match your search criteria.</EmptyStateText>
          </EmptyState>
        ) : (
          filteredApplications.map(application => (
            <ApplicationCard 
              key={application.id} 
              onClick={() => handleViewApplication(application)}
            >
              <ApplicationFlex>
                <LogoContainer>
                  <CompanyLogo 
                    src={application.companyLogo} 
                    alt={`${application.company} logo`} 
                  />
                </LogoContainer>
                <ApplicationContent>
                  <ApplicationHeader>
                    <div>
                      <PositionTitle>{application.position}</PositionTitle>
                      <CompanyName>{application.company}</CompanyName>
                    </div>
                    <StatusBadge status={application.status}>
                      {getStatusIcon(application.status)} {application.status}
                    </StatusBadge>
                  </ApplicationHeader>
                  <ApplicationMeta>
                    <MetaItem>
                      <FaRegCalendarAlt /> <strong>Applied:</strong> {application.appliedDate}
                    </MetaItem>
                    {application.interviewDate && (
                      <MetaItem>
                        <FaUserTie /> <strong>Interview:</strong> {application.interviewDate}
                      </MetaItem>
                    )}
                    {application.startDate && (
                      <MetaItem>
                        <FaRegCalendarAlt /> <strong>Start Date:</strong> {application.startDate}
                      </MetaItem>
                    )}
                  </ApplicationMeta>
                </ApplicationContent>
              </ApplicationFlex>
            </ApplicationCard>
          ))
        )}
      </ApplicationsGrid>
      
      {/* Application Details Modal */}
      {selectedApplication && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                <h2>{selectedApplication.position}</h2>
                <StatusBadge status={selectedApplication.status}>
                  {getStatusIcon(selectedApplication.status)} {selectedApplication.status}
                </StatusBadge>
              </ModalTitle>
              <CloseButton onClick={closeApplicationDetails}>
                &times;
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <ModalCompanyInfo>
                <ModalLogoContainer>
                  <CompanyLogo 
                    src={selectedApplication.companyLogo} 
                    alt={`${selectedApplication.company} logo`} 
                  />
                </ModalLogoContainer>
                <div>
                  <h3>{selectedApplication.company}</h3>
                </div>
              </ModalCompanyInfo>
              
              <StatusCard status={selectedApplication.status}>
                <CardTitle>
                  {getStatusIcon(selectedApplication.status)} Application Status
                </CardTitle>
                <p>{getStatusDescription(selectedApplication.status)}</p>
              </StatusCard>
              
              <DetailsCard>
                <CardTitle>
                  <FaInfoCircle /> Application Details
                </CardTitle>
                <p><strong>Date Applied:</strong> {selectedApplication.appliedDate}</p>
                {selectedApplication.interviewDate && (
                  <p><strong>Interview Date:</strong> {selectedApplication.interviewDate}</p>
                )}
                {selectedApplication.acceptedDate && (
                  <p><strong>Acceptance Date:</strong> {selectedApplication.acceptedDate}</p>
                )}
                {selectedApplication.startDate && (
                  <p><strong>Start Date:</strong> {selectedApplication.startDate}</p>
                )}
              </DetailsCard>
              
              {selectedApplication.status.toLowerCase() === 'accepted' && (
                <NextStepsCard>
                  <CardTitle>
                    <FaCheckCircle /> Next Steps
                  </CardTitle>
                  <p>Please wait for the company to contact you with further instructions and start date information.</p>
                  <p>Once you begin your internship, you'll be able to track your progress and submit reports through the system.</p>
                </NextStepsCard>
              )}
              
              {selectedApplication.status.toLowerCase() === 'finalized' && (
                <NextStepsCard style={{ borderLeft: '4px solid #8a2be2' }}>
                  <CardTitle>
                    <FaInfoCircle /> Next Steps
                  </CardTitle>
                  <p>You have been shortlisted as one of the top candidates for this position.</p>
                  <p>The company is making final decisions and will contact you soon with their decision.</p>
                  <p>No further action is required from you at this time.</p>
                </NextStepsCard>
              )}
            </ModalBody>
            
            <ButtonsContainer>
              <CloseModalButton onClick={closeApplicationDetails}>Close</CloseModalButton>
            </ButtonsContainer>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ApplicationsList;